import { Grid } from '@chakra-ui/react';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { BackToTop } from '@/components/ui/core/BackToTop/BackToTop';
import { FlippingLectureCardOmnisearch } from '@/components/ui/shared/lecture/FlippingLectureCardOmnisearch/FlippingLectureCardOmnisearch';
import { LectureCardSkeleton } from '@/components/ui/shared/lecture/LectureCard/LectureCardSkeleton/LectureCardSkeleton';
import { NoResults } from '@/components/ui/shared/messages/NoResults/NoResults';
import useElement from '@/hooks/useElement';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { OmnisearchThinLecture } from '@/resources/OmnisearchThinLecture';

interface ILectureURLSearchParams {
  query?: string;
}

interface IHubsConferenceLectureGridProps {
  items: Array<OmnisearchThinLecture>;
  isLoadingMore: boolean;
  isReachingEnd: boolean;
  isEmpty: boolean;
  onLoadMore: () => void;
  error: Error;
  pageSize: number;
}

export const LectureGrid: FC<IHubsConferenceLectureGridProps> = ({
  items,
  isLoadingMore,
  isEmpty,
  isReachingEnd,
  onLoadMore,
  error,
  pageSize,
}) => {
  const { element: lectureListElement, ref: lectureListRef } = useElement();
  const { t } = useTranslation();
  const { query } = useQueryParams<ILectureURLSearchParams>({
    query: StringParam,
  });

  if (isEmpty) {
    return <NoResults title={query ? t('common.noResultsForQuery', { query }) : t('common.noResults')} />;
  }

  return (
    <Fragment>
      <Grid
        justifyContent="center"
        ref={lectureListRef}
        templateColumns={{
          base: 'repeat(auto-fill, 335px)',
          lg: 'repeat(auto-fill, 389.3px)',
        }}
        gap={4}
        h="max-content"
      >
        {items.map((lecture) => (
          <FlippingLectureCardOmnisearch key={lecture.id} lecture={lecture} />
        ))}

        {isLoadingMore ? Array.from({ length: pageSize }, (_, i) => <LectureCardSkeleton key={i} />) : null}
      </Grid>

      <InView
        as="span"
        rootMargin="200px"
        onChange={(inView) => {
          if (inView && !error && !isReachingEnd) {
            onLoadMore?.();
          }
        }}
      />

      {!isEmpty && <BackToTop parentTopOffset={lectureListElement?.offsetTop} scrollingOffset={280} />}
    </Fragment>
  );
};
